import { useLazyQuery } from "@apollo/client";
import { EditorContent } from "@tiptap/react";
import equal from "deep-equal";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Select from "react-select";
import AsyncSelect from "react-select/async";

import RichTextAreaMenu from "@components/fields/RichTextAreaMenu";
import styles from "@components/fields/WYSIWYGEditor.module.css";
import Icon from "@components/text/Icon";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMyAdmin from "@hooks/useMyAdmin";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import useRichTextArea from "@hooks/useRichTextArea";
import { MessageTemplateReplacementEnum } from "@typing/Enums";
import {
  ArticleContentTypeEnum,
  GetArticleBasicsDocument,
  GetArticleBasicsQuery,
  GetArticleBasicsQueryVariables,
  GetEmployerBenefitsDocument,
  GetEmployerBenefitsQuery,
  GetEmployerBenefitsQueryVariables,
  GetJourneyWithClientVisibleTasksBasicDocument,
  GetJourneyWithClientVisibleTasksBasicQuery,
  GetJourneyWithClientVisibleTasksBasicQueryVariables,
  GoalStatusEnum
} from "@typing/Generated";
import { editorSelectStyles, selectStyles } from "@utils/formUtils";
import { sortByLabel } from "@utils/modelUtils";
import { isMobileWeb, isNativeApp } from "@utils/platformUtils";

export type EditorSize = "small" | "large" | "medium" | "normal" | "smallish";

type Props = {
  autofocus?: boolean;
  contentRefreshSwitch?: boolean;
  disabled?: boolean;
  extraMenuButtons?: ReactNode[];
  extraStyles?: string;
  forMessaging?: boolean;
  journeyId: string | null;
  onChange?: (html: string) => void;
  placeholder?: string;
  preventEnter?: boolean;
  sharableURLSHidden: boolean;
  sharableURLSWithoutJourneyId?: boolean;
  size?: EditorSize;
  submitButtonSpacing?: boolean;
  templateKeywords?: boolean;
  textMenuHidden: boolean;
  toolbarHidden: boolean;
  value?: string;
};

enum LinkTypes {
  ARTICLES = "articles",
  EMPLOYER_BENEFITS = "employer_benefits",
  GOALS = "goals",
  MY_STORIES = "my_stories",
  PAGES = "pages",
  QUIZZES = "quizzes",
  TASKS = "tasks",
  WEBINARS = "webinars"
}

type Option = {
  groupLabel: string;
  label: string;
  value: string;
};

type OptionGroup = {
  label: string;
  options: Option[];
};

const WYSIWYGEditor = ({
  autofocus = false,
  contentRefreshSwitch = false,
  disabled = false,
  extraMenuButtons = [],
  extraStyles,
  forMessaging = false,
  journeyId,
  onChange,
  placeholder,
  preventEnter = false,
  sharableURLSHidden,
  sharableURLSWithoutJourneyId = false,
  size = "normal",
  submitButtonSpacing = false,
  templateKeywords = false,
  textMenuHidden,
  toolbarHidden,
  value
}: Props) => {
  const [getJourney, getJourneyQuery] = useLazyQuery<
    GetJourneyWithClientVisibleTasksBasicQuery,
    GetJourneyWithClientVisibleTasksBasicQueryVariables
  >(GetJourneyWithClientVisibleTasksBasicDocument);

  const myExpert = useMyExpert();
  const myAdmin = useMyAdmin();

  const [needsContentRefresh, setNeedsContentRefresh] = useState<boolean>(false);

  const controlsOpenerMobile = useOpener(true);

  const editorPlaceholder = (isMobileWeb() || isNativeApp()) && forMessaging ? "Aa" : placeholder;

  const editor = useRichTextArea({
    autofocus,
    disabled,
    onBlur: controlsOpenerMobile.open,
    onChange,
    onFocus: controlsOpenerMobile.close,
    placeholder: editorPlaceholder,
    preventEnter,
    value
  });

  const intl = useIntl();

  const formatOptionLabel = ({ groupLabel, label }: Option) => (
    <div className={styles.label}>
      <div>{label}</div>
      {groupLabel === LinkTypes.EMPLOYER_BENEFITS && (
        <div className={styles.groupLabel}>
          <FormattedMessage id={`pages.expert.sharableUrls.itemTitles.${groupLabel}`} />
        </div>
      )}
    </div>
  );

  useEffect(() => {
    if (journeyId && myExpert?.id) {
      getJourney({
        variables: {
          id: journeyId
        }
      });
    }
  }, [getJourney, journeyId, myExpert?.id]);

  const goals = getJourneyQuery.data?.journey.goals.filter(goal => goal.status === GoalStatusEnum.ACTIVE);

  useEffect(() => {
    if (value !== undefined && value !== editor?.getHTML()) {
      if (editor) {
        editor?.commands.setContent(value);
      } else {
        setNeedsContentRefresh(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentRefreshSwitch]);

  useEffect(() => {
    if (value !== undefined && editor !== null && editor !== undefined && needsContentRefresh === true) {
      editor.commands.setContent(value);
      setNeedsContentRefresh(false);
    }

    if (value === undefined && needsContentRefresh === true) {
      setNeedsContentRefresh(false);
    }
  }, [editor, needsContentRefresh, value]);

  useEffect(() => {
    if (value === "" && value !== editor?.getHTML()) {
      editor?.commands.setContent(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getArticlesQuery = useLazyQueryWithPromise<GetArticleBasicsQuery, GetArticleBasicsQueryVariables>(
    GetArticleBasicsDocument
  );

  const [getEmployerBenefits, employerBenefitsQuery] = useLazyQuery<
    GetEmployerBenefitsQuery,
    GetEmployerBenefitsQueryVariables
  >(GetEmployerBenefitsDocument);

  useEffect(() => {
    if (journeyId && myExpert) {
      getEmployerBenefits({
        variables: {
          category: null,
          count: 30,
          journeyId
        }
      });
    }
  }, [getEmployerBenefits, journeyId, myExpert]);

  useEffect(() => {
    if (
      journeyId &&
      !employerBenefitsQuery.loading &&
      employerBenefitsQuery.data?.employerBenefits.pageInfo.hasNextPage
    )
      employerBenefitsQuery.fetchMore({
        variables: {
          category: null,
          journeyId
        }
      });
  }, [employerBenefitsQuery, journeyId]);

  const handleSetFocus = useCallback(() => {
    editor?.chain().focus().run();
  }, [editor]);

  type DropdownItem = {
    groupLabel: LinkTypes;
    key?: string;
    label: string;
    value: string;
  };

  const linkUrl = useCallback(
    (path: string) => {
      if (path.startsWith("http") || path.startsWith("mailto")) {
        return path;
      }
      return `${import.meta.env.VITE_APP_HOST}/client/journeys/${journeyId !== null ? journeyId : "JOURNEY_ID"}${path}`;
    },
    [journeyId]
  );

  const pageOptions = useMemo<Option[]>(() => {
    const pages = [
      { label: "carePlan", value: "/care-plan" },
      { label: "scheduleConsult", value: "/schedule-consult" },
      { label: "message", value: "/messages" },
      { label: "library", value: "/explore" },
      { label: "marketplace", value: "/explore/marketplace" },
      { label: "community", value: "https://gather.withgrayce.com" },
      { label: "events", value: "/explore/events" },
      { label: "drive", value: "/drive" },
      { label: "profile", value: "/care-profile" },
      { label: "account", value: "/account" },
      { label: "termsOfUse", value: "https://www.withgrayce.com/terms" },
      { label: "privacyPolicy", value: "https://www.withgrayce.com/privacy" },
      { label: "faq", value: "https://www.withgrayce.com/faq" },
      { label: "help", value: "mailto:support@withgrayce.com" }
    ];

    return pages.map(option => ({
      groupLabel: LinkTypes.PAGES,
      label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${option.label}` }),
      value: linkUrl(option.value)
    }));
  }, [intl, linkUrl]);

  const pageMatches = useCallback(
    (searchTerm: string): Option[] =>
      pageOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [pageOptions]
  );

  const quizOptions = [
    { label: "medicationTracker", value: "/medication" },
    { label: "legalDocuments", value: "/legal" },
    { label: "providerTracker", value: "/provider" }
  ].map(option => ({
    groupLabel: LinkTypes.QUIZZES,
    label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${option.label}` }),
    value: linkUrl(option.value)
  }));

  const marketplaceOptions = useMemo<Option[]>(() => {
    if (
      !getJourneyQuery.data?.journey.owner.employer?.hideBenefits &&
      employerBenefitsQuery.data?.employerBenefits.edges
    ) {
      return employerBenefitsQuery.data.employerBenefits?.edges.map(benefitNode => ({
        groupLabel: LinkTypes.EMPLOYER_BENEFITS,
        imageName: "marketplace",
        key: benefitNode?.node?.id as string,
        label: benefitNode?.node?.vendorName as string,
        value: linkUrl(`/explore/marketplace?benefitId=${benefitNode?.node?.id}`)
      }));
    }
    return [];
  }, [
    employerBenefitsQuery.data?.employerBenefits,
    getJourneyQuery.data?.journey.owner.employer?.hideBenefits,
    linkUrl
  ]);

  const quizMatches = useCallback(
    (searchTerm: string): Option[] =>
      quizOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [quizOptions]
  );

  const marketplaceMatches = useCallback(
    (searchTerm: string): Option[] =>
      marketplaceOptions.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [marketplaceOptions]
  );

  const loadArticleTypeOptions = useCallback(
    (searchTerm: string) =>
      new Promise<OptionGroup[] | null>(resolve => {
        if (searchTerm.length > 0 && (myExpert || (myAdmin && sharableURLSWithoutJourneyId))) {
          Promise.all(
            journeyId
              ? [
                  getArticlesQuery({
                    variables: {
                      contentTypes: [
                        ArticleContentTypeEnum.my_story,
                        ArticleContentTypeEnum.text,
                        ArticleContentTypeEnum.video,
                        ArticleContentTypeEnum.webinar
                      ],
                      search: searchTerm
                    }
                  })
                ]
              : [
                  getArticlesQuery({
                    variables: {
                      contentTypes: [
                        ArticleContentTypeEnum.my_story,
                        ArticleContentTypeEnum.text,
                        ArticleContentTypeEnum.video,
                        ArticleContentTypeEnum.webinar
                      ],
                      search: searchTerm
                    }
                  })
                ]
          ).then(data => {
            const modelsByContentType: { [key: string]: { key: string; label: string; value: string }[] } = {};

            if (data[0]?.data?.articles?.edges) {
              Object.values(ArticleContentTypeEnum).forEach(contentType => {
                if (data[0].data.articles.edges) {
                  modelsByContentType[contentType.toString()] = data[0].data.articles.edges
                    ?.filter(articleNode => articleNode?.node?.contentType === contentType)
                    .map(articleNode => ({
                      key: articleNode?.node?.id as string,
                      label: articleNode?.node?.title as string,
                      value: linkUrl(`/articles/${articleNode?.node?.id}`)
                    }));
                }
              });
            }

            resolve([
              {
                label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.ARTICLES}` }),
                options: sortByLabel(
                  modelsByContentType[ArticleContentTypeEnum.text].concat(
                    modelsByContentType[ArticleContentTypeEnum.video]
                  )
                ).map(option => ({ ...option, groupLabel: LinkTypes.ARTICLES }))
              },
              {
                label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.MY_STORIES}` }),
                options: sortByLabel(modelsByContentType[ArticleContentTypeEnum.my_story]).map(option => ({
                  ...option,
                  groupLabel: LinkTypes.MY_STORIES
                }))
              },
              {
                label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.WEBINARS}` }),
                options: sortByLabel(modelsByContentType[ArticleContentTypeEnum.webinar]).map(option => ({
                  ...option,
                  groupLabel: LinkTypes.WEBINARS
                }))
              }
            ]);
          });
        } else {
          resolve(null);
        }
      }),
    [sharableURLSWithoutJourneyId, getArticlesQuery, intl, journeyId, linkUrl, myAdmin, myExpert]
  );

  const dropdownOptionsForSearch = useCallback(
    (searchTerm: string) =>
      new Promise<OptionGroup[] | null>(resolve => {
        loadArticleTypeOptions(searchTerm).then(articleOptions => {
          const result: OptionGroup[] = [
            {
              label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.EMPLOYER_BENEFITS}` }),
              options: marketplaceMatches(searchTerm).map(option => ({ ...option }))
            },
            {
              label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.PAGES}` }),
              options: pageMatches(searchTerm).map(option => ({ ...option }))
            },
            {
              label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.QUIZZES}` }),
              options: quizMatches(searchTerm).map(option => ({ ...option }))
            }
          ];

          if (articleOptions) {
            articleOptions.forEach(option => result.push(option));
          }

          const goalItems = goals
            ?.map(goal => ({
              key: goal.id,
              label: goal.title ?? "",
              value: linkUrl(`/care-plan/goals/${goal.id}`)
            }))
            .filter(task => task.label.toLowerCase().includes(searchTerm.toLowerCase()));

          if (goalItems) {
            result.push({
              label: intl.formatMessage({ id: `pages.expert.sharableUrls.itemTitles.${LinkTypes.GOALS}` }),
              options: sortByLabel(goalItems).map(option => ({ ...option, groupLabel: LinkTypes.GOALS }))
            });
          }

          resolve(result);
        });
      }),
    [goals, intl, linkUrl, loadArticleTypeOptions, marketplaceMatches, pageMatches, quizMatches]
  );

  const loadDropdownOptions = useCallback(
    (searchTerm: string, callback: (options: OptionGroup[]) => void) => {
      dropdownOptionsForSearch(searchTerm).then(options => {
        callback(options ?? []);
      });
    },
    [dropdownOptionsForSearch]
  );

  const handleDropdownChange = useCallback(
    (selection: DropdownItem) => {
      if (editor) {
        const target =
          selection.value.startsWith("https://www.withgrayce.com") || selection.value.startsWith("mailto")
            ? "_blank"
            : "_self";
        editor.commands.insertContent(`<a href="${selection.value}" target="${target}">${selection.label}</a>`);
        editor.chain().focus().run();
      }
    },
    [editor]
  );

  const templateKeywordOptions = Object.values(MessageTemplateReplacementEnum).map(replacement => ({
    label: intl.formatMessage({ id: `pages.expert.sharableUrls.templateKeywords.${replacement}` }),
    value: replacement
  }));

  const handleInsertText = useCallback(
    (value: string) => {
      if (editor) {
        const transaction = editor.state.tr.insertText(value);
        editor.view.dispatch(transaction);
        editor.chain().focus().run();
      }
    },
    [editor]
  );

  const editorClassName = useMemo(() => {
    const parts = [styles.editor];
    parts.push(sharableURLSHidden ? styles.sharableURLSHidden : "");
    parts.push(toolbarHidden || textMenuHidden ? styles.toolbarHidden : "");
    parts.push(editor?.isFocused ? styles.hasFocus : "");
    parts.push(submitButtonSpacing ? styles.submitButtonSpacing : "");
    parts.push(forMessaging ? styles.forMessaging : "");
    parts.push(extraStyles ? extraStyles : "");
    return parts.join(" ");
  }, [
    editor?.isFocused,
    extraStyles,
    forMessaging,
    sharableURLSHidden,
    submitButtonSpacing,
    textMenuHidden,
    toolbarHidden
  ]);

  const editorInteriorClassName = useMemo(() => {
    if (forMessaging) return styles.editorInteriorMessaging;

    const parts = [styles.editorInterior];
    switch (size) {
      case "small":
        parts.push(styles.interiorHeightSmall);
        break;
      case "smallish":
        parts.push(styles.interiorHeightSmallish);
        break;
      case "medium":
        parts.push(styles.interiorHeightMedium);
        break;
      case "large":
        parts.push(styles.interiorHeightLarge);
        break;
    }
    return parts.join(" ");
  }, [forMessaging, size]);

  const [defaultOptions, setDefaultOptions] = useState<OptionGroup[]>([]);

  useEffect(() => {
    dropdownOptionsForSearch("").then(options => {
      if (!equal(options, defaultOptions)) {
        setDefaultOptions(options ?? []);
      }
    });
  }, [defaultOptions, dropdownOptionsForSearch]);

  const linkComponent = (
    <AsyncSelect
      defaultOptions={defaultOptions}
      formatOptionLabel={formatOptionLabel}
      isDisabled={disabled}
      isSearchable
      loadOptions={loadDropdownOptions}
      menuPlacement="top"
      menuPortalTarget={document.body}
      onChange={option => {
        const typedOption = option as unknown as DropdownItem;
        handleDropdownChange(typedOption);
      }}
      placeholder={intl.formatMessage({ id: "pages.expert.sharableUrls.placeholder" })}
      styles={{
        ...editorSelectStyles,
        menu: (base: any) => ({ ...base, position: "absolute", right: 80 }),
        menuPortal: (base: any) => ({ ...base, width: 270, zIndex: 9999 })
      }}
      tabIndex={disabled ? -1 : 0}
      value={null}
    />
  );

  const showSharableLinks = useMemo(() => {
    if (toolbarHidden || sharableURLSHidden) return false;

    if (journeyId) return true;

    if ((myExpert || myAdmin) && sharableURLSWithoutJourneyId) return true;

    return false;
  }, [journeyId, sharableURLSWithoutJourneyId, myAdmin, myExpert, sharableURLSHidden, toolbarHidden]);

  return (
    <>
      {editor && (
        <div className={editorClassName}>
          {extraMenuButtons.length > 0 && (
            <RichTextAreaMenu
              controlsOpenerMobile={controlsOpenerMobile}
              disabled={disabled}
              editor={editor}
              expertLinks={showSharableLinks ? linkComponent : undefined}
              extraMenuButtons={extraMenuButtons}
              forMessaging={forMessaging}
              textControlsHidden={toolbarHidden || textMenuHidden}
            />
          )}

          <div
            className={editorInteriorClassName}
            onClick={handleSetFocus}
            onScroll={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {size === "smallish" && placeholder && (
              <div className={styles.placeholderIconContainer}>
                <Icon color="gray-500" icon="penToSquare" />
              </div>
            )}
            <EditorContent
              disabled={disabled}
              editor={editor}
              placeholder={intl.formatMessage({ id: "dictionary.typeHere" })}
            />
          </div>
          {extraMenuButtons.length === 0 && (
            <RichTextAreaMenu
              controlsOpenerMobile={controlsOpenerMobile}
              disabled={disabled}
              editor={editor}
              expertLinks={showSharableLinks ? linkComponent : undefined}
              extraMenuButtons={extraMenuButtons}
              forMessaging={forMessaging}
              textControlsHidden={toolbarHidden || textMenuHidden}
            />
          )}
        </div>
      )}

      {templateKeywords && (
        <Select
          isDisabled={disabled}
          isSearchable
          menuPlacement="top"
          menuPortalTarget={document.body}
          onChange={option => {
            if (option) {
              handleInsertText(option.value);
            }
          }}
          options={templateKeywordOptions}
          placeholder={intl.formatMessage({ id: "pages.expert.sharableUrls.templateKeywords.placeholder" })}
          styles={selectStyles()}
          tabIndex={disabled ? -1 : 0}
          value={null}
        />
      )}
    </>
  );
};

export default WYSIWYGEditor;
